import React, { useState, useEffect, useMemo } from 'react';
import Contacts from './contacts/Contacts';
import Templates from './templates/Templates';
import Send from './send/Send';
import PitchBodies from './pitchBodies/PitchBodies';
import SendPitch from './sendPitch/SendPitch';
import { useAppSelector } from '../../../hooks/redux';
import SegmentedTabs from '../../ui/SegmentedTabs';

interface EmailTabsProps {
  initialTab?: string | null;
  searchParams?: URLSearchParams;
  setSearchParams?: (params: URLSearchParams, options?: any) => void;
}

const EmailTabs: React.FC<EmailTabsProps> = ({ initialTab, searchParams, setSearchParams }) => {
  const { user } = useAppSelector((state) => state.auth);
  const isAdmin = user?.role === 'admin';

  // Admin sees templates and pitch-bodies tabs, regular users see all tabs
  const availableTabs = useMemo(() => {
    return isAdmin ? ['templates', 'pitch-bodies'] : ['contacts', 'templates', 'send-pitch', 'pitch-bodies', 'logs'];
  }, [isAdmin]);

  // Get initial tab from URL or default
  const getInitialActiveTab = (): string => {
    const urlTab = searchParams?.get('tab');
    if (urlTab && availableTabs.includes(urlTab)) {
      return urlTab;
    }

    if (initialTab && availableTabs.includes(initialTab)) {
      return initialTab;
    }

    return availableTabs[0] || 'contacts';
  };

  const [activeTab, setActiveTab] = useState<string>(getInitialActiveTab());

  // Update active tab when URL changes
  useEffect(() => {
    const urlTab = searchParams?.get('tab');

    if (urlTab && availableTabs.includes(urlTab)) {
      setActiveTab(urlTab);
    } else if (initialTab && availableTabs.includes(initialTab)) {
      setActiveTab(initialTab);
    }
  }, [searchParams, initialTab]);

  const handleTabClick = (tabKey: string) => {
    setActiveTab(tabKey);

    if (setSearchParams) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', tabKey);
      setSearchParams(newSearchParams, { replace: true });
    }
  };

  // Tab config based on user role - admin sees templates and pitch bodies, users see all
  const tabConfig = useMemo(() => {
    if (isAdmin) {
      return [
        { name: 'Email Templates', key: 'templates' },
        { name: 'Pitch Bodies', key: 'pitch-bodies' },
      ];
    }
    return [
      { name: 'Contacts', key: 'contacts' },
      { name: 'Email Templates', key: 'templates' },
      { name: 'Send Pitch', key: 'send-pitch' },
      { name: 'Pitch Bodies', key: 'pitch-bodies' },
      { name: 'Email Logs', key: 'logs' },
    ];
  }, [isAdmin]);

  // Render component based on active tab
  const renderActiveComponent = () => {
    switch (activeTab) {
      case 'contacts':
        return <Contacts />;
      case 'templates':
        return <Templates />;
      case 'send-pitch':
        return <SendPitch />;
      case 'pitch-bodies':
        return <PitchBodies />;
      case 'logs':
        return <Send />;
      default:
        return isAdmin ? <Templates /> : <Contacts />;
    }
  };

  return (
    <div className="w-full overflow-hidden">
      {/* Tabs Navigation - same primitive as /analytics and artist details. */}
      <div className="w-full overflow-x-auto bg-white rounded-t-lg scrollbar-hide">
        <SegmentedTabs
          className="px-2 min-w-max"
          ariaLabel="Email sections"
          value={activeTab}
          onChange={handleTabClick}
          items={tabConfig.map((tab) => ({ key: tab.key, label: tab.name }))}
        />
      </div>

      {/* Render the active tab's component */}
      <div className="w-full mt-4 sm:mt-6 overflow-x-hidden">
        {renderActiveComponent()}
      </div>
    </div>
  );
};

export default EmailTabs;
